import { Button, Col, Row, Space, Typography } from "antd";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StatusIcon from "../../assets/icons/status-circle.svg";
import { ACCEPTED, CREATOR, DELIVERED, FAN, FILE, MESSAGE_TIME_FORMAT, PENDING, REJECTED, TEXT, THUMBNAIL_TYPES } from "../../helpers/constant";
import { IMessage } from "../../helpers/types";
import { authService } from "../../services";
import AvatarComponent from "../AvatarComponent";
import IdentificationComponent from "../IdentificationComponent/IdentificationComponent";
import MediaComponent from "../MediaComponent";
import MessagePriceComponent from "../MessagePriceComponent";
import "./index.less";

type props = {
  message?: IMessage;
  user?: string;
  userRole?: string;
  acceptMessage?: any;
  rejectMessage?: any;
  hideStatus?: boolean;
  username?: string;
};

const formatTextWithMentions = (text: string, navigate: (path: string) => void, interlocutorUsername?: string): React.ReactNode[] => {
  // First replace /name with the interlocutor's username
  let processedText = text;
  if (interlocutorUsername) {
    processedText = text.replace(/\/name/g, interlocutorUsername);
  }

  // Then split by @ mentions and apply bold formatting with navigation
  const parts = processedText.split(/(@\w+)/g);
  return parts.map((part, index) => {
    if (part.startsWith("@")) {
      const username = part.substring(1); // Remove the @ symbol
      return (
        <strong key={index} onClick={() => navigate(`/p/${username}`)} style={{ cursor: "pointer", color: "#1d9bf0" }}>
          {part}
        </strong>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const IncomeMessageComponent: React.FC<props> = ({ message, user, userRole, acceptMessage, rejectMessage, hideStatus, username }) => {
  const { t } = useTranslation();
  const messageTime = dayjs(message?.createdAt);
  const navigate = useNavigate();
  const mediaThumbs = useMemo(() => {
    if (!message?.medias || isEmpty(message.medias))
      return [] as Array<{
        fileId: string;
        imageUrl: string | null;
        isPrivate: boolean;
        isVideo: boolean;
        isAudio: boolean;
        url?: string;
      }>;
    const isMsgAcceptedOrDelivered = [ACCEPTED, DELIVERED].includes(message?.status ?? "");
    return message.medias.map((media) => {
      const isPrivate = !isMsgAcceptedOrDelivered && !!media.isPrivate;
      const isVideo = !!media?.url?.includes(".mp4");

      // Comprehensive audio detection logic
      const isAudio =
        media.isAudio === true ||
        media.url?.toLowerCase().includes(".mp3") ||
        media.url?.toLowerCase().includes(".wav") ||
        media.url?.toLowerCase().includes(".m4a") ||
        media.url?.toLowerCase().includes(".ogg") ||
        media.url?.toLowerCase().includes(".aac") ||
        media.url?.toLowerCase().includes(".webm") ||
        (media.url && media.url.includes("audio%2f"));

      let imageUrl: string | null = null;
      if (isPrivate) {
        imageUrl = media.thumbnails?.find((thumb: any) => thumb.type === THUMBNAIL_TYPES.BLURRED)?.absolutePath ?? null;
      } else {
        imageUrl = media.thumbnails?.find((thumb: any) => thumb.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath ?? null;
      }
      return {
        fileId: media.fileId,
        imageUrl,
        isPrivate,
        isVideo,
        isAudio,
        url: media.url,
      };
    });
  }, [message?.medias, message?.status]);

  const name = authService.getUser()?.firstName;
  const shouldShowMessageTime = useMemo(() => {
    if (!messageTime) return false;

    if (userRole === CREATOR) {
      return ![PENDING, ACCEPTED].includes(message?.status ?? "");
    }

    if (userRole === FAN) {
      if (message?.type === FILE) {
        return ![PENDING, DELIVERED, ACCEPTED].includes(message?.status ?? "");
      }
      if (message?.type === TEXT) {
        return ![PENDING, DELIVERED].includes(message?.status ?? "");
      }
    }

    return false;
  }, [messageTime, userRole, message?.status, message?.type]);

  return (
    <div className={`incomeMessage ${message?.medias && !isEmpty(message?.medias) ? "min-width-media" : ""}`}>
      {user && <AvatarComponent image={user} size={23} />}
      <Row className="incomeMessageBox">
        <IdentificationComponent identifications={message?.taggedCreators || []} role={FAN} />
        <Space className="w-full gap-10" direction="vertical">
          {message?.mediaPushPrice !== undefined && <MessagePriceComponent message={message} income={true} />}

          {mediaThumbs.length > 0 && (
            <Row className={"mediaRow"}>
              {mediaThumbs.map((m) => (
                <MediaComponent
                  key={m.fileId}
                  width={68}
                  height={68}
                  borderRadius={11}
                  image={m.imageUrl || ""}
                  isPrivate={m.isPrivate}
                  isVideo={m.isVideo}
                  isAudio={!!m.isAudio}
                  url={m.url}
                  username={username!}
                  shouldAddWatermark={true}
                  displayVideo={message?.status === ACCEPTED}
                />
              ))}
            </Row>
          )}
          <Row align="middle" style={{ width: "100%" }}>
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                {message?.text && (
                  <Row>
                    <Typography.Text className="message-style">
                      {message?.taggedCreators &&
                      Array.isArray(message.taggedCreators) &&
                      message.taggedCreators.length > 0 &&
                      message.text?.includes("@")
                        ? formatTextWithMentions(message.text, navigate, name)
                        : message?.text?.includes("/name") && name
                          ? message?.text.replace(/\/name/g, name)
                          : message?.text}
                    </Typography.Text>
                  </Row>
                )}
                {shouldShowMessageTime && (
                  <Typography.Text
                    className={`message-time text-white-color ${message?.text && message.text?.length <= 45 ? "message-time-margin" : ""}`}
                    style={{ marginLeft: "auto", paddingLeft: "8px" }}
                  >
                    {messageTime.format(MESSAGE_TIME_FORMAT)}
                  </Typography.Text>
                )}
              </div>
            </Col>
          </Row>
          {message?.status === PENDING && (
            <Row>
              <Space style={{ width: "100%", justifyContent: "space-between", alignItems: "flex-end" }}>
                <Col style={{ display: "flex" }}>
                  <Button className="accept-media-button" onClick={() => acceptMessage(message)}>
                    {t("accept")}
                  </Button>
                  <Button className="reject-media-button" onClick={() => rejectMessage(message._id)} style={{ marginLeft: "10px" }}>
                    {t("refuse")}
                  </Button>
                </Col>
                <Col>
                  {messageTime && (
                    <Typography.Text className="message-time text-white-color" style={{ paddingLeft: "10px" }}>
                      {messageTime.format(MESSAGE_TIME_FORMAT)}
                    </Typography.Text>
                  )}
                </Col>
              </Space>
            </Row>
          )}

          {![PENDING, REJECTED].includes(message?.status!) && (userRole !== FAN || message?.type === FILE) && !hideStatus && (
            <Row className="mt-10">
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Col>
                  <img src={StatusIcon} alt={message?.status} />
                  <Typography.Text className="font-14-bold text-white-color pl-4">
                    {t(message?.status!)}{" "}
                    {isEmpty(message?.medias) && message?.status === ACCEPTED && message?.type === FILE ? `(${t(PENDING)})` : ""}
                  </Typography.Text>
                </Col>
                <Col>
                  {messageTime && (
                    <Typography.Text className="message-time text-white-color">{messageTime.format(MESSAGE_TIME_FORMAT)}</Typography.Text>
                  )}
                </Col>
              </Space>
            </Row>
          )}
        </Space>
      </Row>
    </div>
  );
};

export default React.memo(IncomeMessageComponent);
